/**
 * SCHRITT 1 NACH GUTSCHRIFTEN (.388).
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (17.10.2026)
 *   „Ist der Aufbau auch falsch, dass wir uns nicht nach den Firmen
 *    orientieren, sondern den Eingang der Gutschriften auflisten. Gutschrift,
 *    Gutschriftnummer von mir aus, Eingang, Adresse und dann wer da
 *    gearbeitet hat, damit wir dann rausschicken können. ... Das macht es mir
 *    wahrscheinlich auch einfacher, das dann mit den E-Mails abzugleichen."
 *   „Wird Chip grün. Wenn sie den noch nicht geschrieben haben, beziehungsweise
 *    du es nicht zuordnen konntest, ist der Chip noch rot von der Firma."
 *   „bevor ich den Push rausschicke, die Funktion, dass ich prüfen kann und
 *    dass die Positionen abgeändert werden müssen beim Push, damit wir nicht
 *    das Duplikat haben, dass eine Rechnung doppelt geschrieben wird."
 *   „Wir bauen eine Zeile mit zwei Chips zu den zwei Firmen mit dem bekannten
 *    Ausklappmenü und den Vorschlägen, ob überhaupt abgerechnet werden muss
 *    oder nicht ... in einer Zeile, damit wir die Mails abgleichen können."
 *
 * EINE ZEILE JE AUFTRAG, das ist eine Gutschrift-Mail der Telekom. Oben
 * Belegnummer, Eingang und Anschrift; daran je Firma und Gewerk ein Chip:
 *   rot      keine Rechnung da, Push noch nicht raus
 *   orange   Push raus (mit Datum), Rechnung noch nicht da
 *   grün     Rechnung liegt vor (mit Nummer): KEIN Push mehr, nur prüfen
 *   grau     wahrscheinlich keine Leistung: erst entscheiden
 * Ein Klick auf den Chip klappt das bekannte Menü auf (Positionsblock): die
 * drei Spalten, die drei Wege für die Nachricht, „Keine Rechnung nötig". Bei
 * Abweichungen gibt es den Push-Knopf NUR im aufgeklappten Menü: erst sehen,
 * dann schicken. Und wo eine Rechnung liegt, gibt es gar keinen.
 */
import { useMemo, useState } from 'react';
import { useMerkState } from './merkplatz.ts';
import { api, darf, datum, euro } from '../api.ts';
import type { Anmelder } from '../api.ts';
import { ausFehler } from './Formular.tsx';
import { NummernKopieren, RechnungChip } from './Rechnungspruefung.tsx';
import { anschriftVon, passtSuche } from './Schluesselsuche.tsx';
import { Frage, Positionsblock, lageVon, mehrZeilen, nichtPos, nichtZeilen, pushVorschau, voll, zeileGehoertZu } from './Abrechnungsfreigabe.tsx';
import { fragen, eingeben } from './rueckfrage.tsx';

const GEWERK: Record<string, string> = { tiefbau: 'Tiefbau', montage: 'Montage' };
const schluessel = (z: any) => `${z.auftrag_id}|${z.subunternehmer_id}|${z.gewerk_gruppe}|${z.gutschrift_id ?? ''}`;

type Chipfarbe = 'rot' | 'orange' | 'gruen' | 'grau';
function chipfarbe(z: any): Chipfarbe {
  if (z.rechnung_da) return 'gruen';
  if (lageVon(z) === 'ohne') return 'grau';
  return 'rot';
}

export interface Gutschriftgruppe {
  /** gutschrift_id, oder `auftrag_id` bei Zeilen ohne Gutschrift (Altbestand). */
  schluessel: string;
  auftrag_id: string;
  gutschrift_id: string | null;
  nummer: string | null;
  zeilen: any[];          /* bereit, noch ohne Freigabe */
  angefordert: any[];     /* Freigaben der letzten 60 Tage zu dieser Gutschrift */
  eingang: string | null;
  kopf: any;
}

/**
 * EINE ZEILE JE GUTSCHRIFT (.389). ORIGINALTEXT MARVIN (17.10.2026): „Sowas
 * geht nicht, du kannst die nicht zusammenfassen. Die hat ein
 * unterschiedliches Eingangsdatum, ganz klar, definitiv." Zwei Gutschriften
 * zum selben Auftrag sind zwei Zeilen; die Firmen hängen als Chips an der
 * Gutschrift, zu der sie gehören. Angeforderte (Push raus, Rechnung fehlt)
 * stehen als eigene Zeile, wenn ihre Gutschrift sonst nirgends steht, damit
 * sie oben wählbar sind. Neueste zuerst.
 */
export function gruppiere(zeilen: any[], zuletzt: any[]): Gutschriftgruppe[] {
  const m = new Map<string, Gutschriftgruppe>();
  const schl = (z: any) => String(z.gutschrift_id ?? `A:${z.auftrag_id}`);
  for (const z of zeilen) {
    const k = schl(z);
    const g = m.get(k) ?? { schluessel: k, auftrag_id: z.auftrag_id, gutschrift_id: z.gutschrift_id ?? null, nummer: z.gutschrift_nummer ?? null,
                           zeilen: [], angefordert: [], eingang: z.gutschrift_eingang ?? z.gutschrift_zuletzt ?? null, kopf: z };
    g.zeilen.push(z);
    m.set(k, g);
  }
  for (const f of zuletzt) {
    if (f.wie === 'bestand') continue;
    const k = schl(f);
    const g = m.get(k) ?? { schluessel: k, auftrag_id: f.auftrag_id, gutschrift_id: f.gutschrift_id ?? null, nummer: f.gutschrift_nummer ?? null,
                           zeilen: [], angefordert: [], eingang: f.gutschrift_eingang ?? null, kopf: f };
    g.angefordert.push(f);
    m.set(k, g);
  }
  return [...m.values()].sort((a, b) => String(b.eingang ?? '').localeCompare(String(a.eingang ?? '')));
}

export function Gutschriftliste({ ich, d, suche, laden, setHinweis, setFehler, setPruefe, oeffne, entferne }: {
  ich?: Anmelder; d: any; suche: string; laden: () => Promise<void>;
  setHinweis: (t: string | null) => void; setFehler: (f: any) => void; setPruefe: (id: string | null) => void;
  /** Öffnet die Akte des Auftrags (.397): die Anschrift im Kopf ist der Sprung dorthin. */
  oeffne?: (id: string, reiter?: string) => void;
  /** Nimmt Zeilen sofort aus der Liste (.398), bevor das Nachladen sie ohnehin wegräumt. */
  entferne?: (zs: any[]) => void;
}) {
  /* MIT GEDÄCHTNIS (.398), siehe `merkplatz.ts`: aufgeklappte Zeilen, Auswahl,
     Filter und Firmenchip überleben den Sprung in die Akte und zurück. */
  const [auf, setAuf] = useMerkState<Set<string>>('gl.auf', () => new Set());
  const [gewaehlt, setGewaehlt] = useMerkState<Set<string>>('gl.gewaehlt', () => new Set());
  /* DIE ANGEFORDERTEN SIND OBEN WÄHLBAR (.389). Marvin, 17.10.2026: „wenn du
     sagst 55 angefordert, dann habe ich aber oben im oberen Bereich gar keine
     Möglichkeit, die auszuwählen." Gewählt wird die Freigabe; „Erinnern"
     schickt je Firma einen Push, der auf den ersten verweist. */
  const [gewaehltAngefordert, setGewaehltAngefordert] = useMerkState<Set<string>>('gl.gewaehltAngefordert', () => new Set());
  const [mehr, setMehr] = useState<Set<string>>(new Set());
  const [ohne, setOhne] = useState<Set<string>>(new Set());
  /* NICHT DEINE POSITION (.410), überlebt den Sprung in die Akte. */
  const [nicht, setNicht] = useMerkState<Set<string>>('gl.nicht', () => new Set());
  /* FREITEXT JE CHIP (.399), überlebt den Sprung in die Akte. */
  const [hinweise, setHinweise] = useMerkState<Record<string, string>>('gl.hinweise', () => ({}));
  const [frage, setFrage] = useState<string | null>(null);
  const [filter, setFilter] = useMerkState<'alle' | 'offen' | 'abweichung' | 'ohne' | 'da' | 'angefordert' | 'alt'>('gl.filter', 'alle');
  const [firma, setFirma] = useMerkState<string | null>('gl.firma', null);
  const [laeuft, setLaeuft] = useState(false);
  const darfGeld = !!ich && darf(ich, 'abrechnung.geld');

  const gruppen = useMemo(() => gruppiere(d?.zeilen ?? [], d?.zuletzt ?? []), [d]);
  const firmen = useMemo(() => [...new Set((d?.zeilen ?? []).map((z: any) => String(z.firma)))].sort((a, b) => a.localeCompare(b, 'de')), [d]);

  type Filter = 'alle' | 'offen' | 'abweichung' | 'ohne' | 'da' | 'angefordert' | 'alt';
  /* ALTBESTAND (.390): vor dem Stichtag eingegangen, nur unter seinem Filter. */
  const istAlt = (g: Gutschriftgruppe) => g.zeilen.length > 0 && g.zeilen.every((z) => z.altbestand === true);
  const trifft = (g: Gutschriftgruppe, f: Filter): boolean => {
    if (f === 'alt') return istAlt(g);
    if (istAlt(g)) return false;
    switch (f) {
      case 'offen': return g.zeilen.some((z) => chipfarbe(z) === 'rot');
      case 'abweichung': return g.zeilen.some((z) => chipfarbe(z) === 'rot' && (z.abweichung_zeilen ?? []).length > 0);
      case 'ohne': return g.zeilen.some((z) => chipfarbe(z) === 'grau');
      case 'da': return g.zeilen.some((z) => z.rechnung_da) || g.angefordert.some((z) => z.rechnung_da);
      case 'angefordert': return g.angefordert.some((z) => !z.rechnung_da);
      default: return true;
    }
  };
  const inFirma = (g: Gutschriftgruppe) => !firma || g.zeilen.some((z) => z.firma === firma) || g.angefordert.some((z) => z.firma === firma);
  const sicht = gruppen.filter((g) => inFirma(g) && trifft(g, filter)
    && [...g.zeilen, ...g.angefordert].some((z) => passtSuche(suche, z.kls_id, z.sm_nummer, anschriftVon(z), z.firma, z.rechnung_nummer, g.nummer, z.belegnummer)));
  const zaehl = (f: Filter) => gruppen.filter((g) => inFirma(g) && trifft(g, f)).length;
  /* DIE ZAHL AM FIRMENCHIP (.410). Marvin, 19.10.2026: „hier fehlen die
     Zahlen, wie viel bei jeder Firma sozusagen dann da drinnen stehen, wenn
     gefiltert werden." Je Firma die Gutschriften im gerade gewählten Filter,
     unabhängig vom gewählten Firmenchip, damit man sieht, wohin ein Wechsel
     führt. */
  const zaehlFirma = (f: string) => gruppen.filter((g) => trifft(g, filter)
    && (g.zeilen.some((z) => z.firma === f) || g.angefordert.some((z) => z.firma === f))).length;

  const schalteIn = (set: Set<string>, setze: (s: Set<string>) => void, k: string) => {
    const n = new Set(set); if (n.has(k)) n.delete(k); else n.add(k); setze(n);
  };
  /* .612: nur angehakte Positionen stehen im Text; ohne Änderung kommt der kurze Standardtext (pushVorschau). */
  const vorschauZeile = (z: any) => {
    const aus = nichtPos(z, schluessel(z), nicht);
    const nurAngehakt = (zl: string[]) => zl.filter((s) => !aus.some((a) => zeileGehoertZu(s, a)));
    return { ...(ohne.has(schluessel(z)) ? { ...z, abweichung_zeilen: [] }
      : { ...z, abweichung_zeilen: nurAngehakt([...(z.abweichung_zeilen ?? []), ...(mehr.has(schluessel(z)) ? mehrZeilen(z) : [])]) }),
      hinweis: hinweise[schluessel(z)] ?? '', nicht_zeilen: [] };
  };

  /* DER PUSH, je Chip oder für die Auswahl. Der Server fasst je Firma zu EINER
     Nachricht zusammen und hält eine Zeile mit Rechnung ohne Push fest. */
  async function los(zs: any[], bestand = false) {
    const eintraege = zs.map((z: any) => ({ auftrag_id: z.auftrag_id, subunternehmer_id: z.subunternehmer_id, gutschrift_id: z.gutschrift_id ?? null,
                                            gewerk_gruppe: z.gewerk_gruppe, mehr: mehr.has(schluessel(z)), wie_eingereicht: ohne.has(schluessel(z)),
                                            hinweis: (hinweise[schluessel(z)] ?? '').trim() || null,
                                            nicht: nichtZeilen(z, schluessel(z), nicht), nicht_pos: nichtPos(z, schluessel(z), nicht) }));
    if (!eintraege.length) return;
    const firmenText = [...new Set(zs.map((z) => z.firma))].join(', ');
    if (!await fragen(bestand
      ? `${eintraege.length} als erledigt festhalten? ${firmenText} bekommt KEINE Nachricht.`
      : `${firmenText}: ${eintraege.length} zur Abrechnung freigeben? Je Firma EINE Push-Nachricht in Glocke und Telegram, `
        + 'mit den Positionen, die auf der Rechnung anders stehen müssen als im Aufmaß. Nur Mengen, nie Preise.')) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const e = await api.freigeben(eintraege, bestand);
      setHinweis(e.klartext);
      setGewaehlt(new Set([...gewaehlt].filter((k) => !zs.some((x) => schluessel(x) === k))));
      /* SOFORT WEG, DANN NACHLADEN (.398): Marvin, 17.10.2026: „da braucht er
         sehr, sehr lange zum Laden, als dass innerhalb von einer Sekunde das
         Ganze so abgearbeitet ist." Die Zeile verschwindet mit der Antwort;
         der orange Chip kommt mit dem Nachladen. */
      entferne?.(zs);
      setLaeuft(false);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }
  async function ohneRechnung(z: any) {
    const grund = await eingeben(`${z.firma}, ${GEWERK[z.gewerk_gruppe]} an ${voll(z)}: ohne Rechnung erledigen? `
      + 'Die Firma bekommt keine Nachricht. Grund (kommt in die Akte):', z.ohne_leistung ? 'keine Leistung erbracht' : '');
    if (grund === null) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const e = await api.freigeben([{ auftrag_id: z.auftrag_id, subunternehmer_id: z.subunternehmer_id, gewerk_gruppe: z.gewerk_gruppe, gutschrift_id: z.gutschrift_id ?? null }],
                                    true, grund.trim() || 'keine Leistung erbracht');
      setHinweis(e.klartext);
      entferne?.([z]);
      setLaeuft(false);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  async function erinnern(ids: string[]) {
    if (!ids.length) return;
    if (!await fragen(`${ids.length} ${ids.length === 1 ? 'Firma' : 'Firmen'} an die angeforderte Rechnung erinnern? Je Freigabe ein kurzer Push, der auf die erste Anforderung verweist.`)) return;
    setLaeuft(true); setFehler(null); setHinweis(null);
    const texte: string[] = [];
    try {
      for (const id of ids) texte.push((await api.freigabeErinnern(id)).klartext);
      setHinweis(texte.join(' '));
      setGewaehltAngefordert(new Set());
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }
  const gewaehltZeilen = (d?.zeilen ?? []).filter((z: any) => gewaehlt.has(schluessel(z)) && chipfarbe(z) === 'rot');
  const angeforderteOffen = (d?.zuletzt ?? []).filter((a: any) => a.wie !== 'bestand' && !a.rechnung_da);
  const gewaehltErinnern = angeforderteOffen.filter((a: any) => gewaehltAngefordert.has(a.id));
  const alleRot = (d?.zeilen ?? []).filter((z: any) => !z.altbestand && chipfarbe(z) === 'rot' && (z.abweichung_zeilen ?? []).length === 0 && lageVon(z) === 'passt');

  return (
    <div className="gl">
      <div className="ga-leiste">
        <div className="ga-filter">
          {/* SECHS FILTER (.392). Marvin: „geprüft oder ungeprüft" - die Abweichung ist kein eigener Korb mehr, sie steht am Chip. */}
          {([['alle', 'alle'], ['offen', 'ungeprüft, Push noch nicht raus'], ['angefordert', 'angefordert, Rechnung fehlt'], ['da', 'Rechnung schon da'], ['ohne', 'keine Rechnung nötig?'], ['alt', 'Altbestand vor dem Stichtag']] as const).map(([f, t]) => {
            const n = zaehl(f);
            if (f !== 'alle' && !n) return null;
            return (
              <button key={f} type="button" aria-pressed={filter === f}
                      className={`ga-chip ${f === 'offen' ? 'rot' : f === 'da' ? 'gruen' : f === 'ohne' || f === 'angefordert' ? 'orange' : ''} ${filter === f ? 'an' : ''}`}
                      onClick={() => setFilter(f)}>{t} {n}</button>
            );
          })}
        </div>
        <div className="ga-filter">
          {firmen.map((f) => (
            <button key={f} type="button" aria-pressed={firma === f} className={`ga-chip ${firma === f ? 'an' : ''}`}
                    onClick={() => setFirma(firma === f ? null : f)}>{f} {zaehlFirma(f)}</button>
          ))}
        </div>
        <div className="ga-tun">
          {alleRot.length > 0 && (
            <button className="knopf klein" disabled={laeuft}
                    title="Alle, bei denen die Gutschrift das Aufmaß deckt: ohne Positionen, je Firma eine Nachricht"
                    onClick={() => los(alleRot)}>Alle ohne Abweichung informieren ({alleRot.length})</button>
          )}
          <button className="knopf haupt" disabled={!gewaehltZeilen.length || laeuft} onClick={() => los(gewaehltZeilen)}>
            {laeuft ? 'läuft …' : `Ausgewählte informieren (${gewaehltZeilen.length})`}
          </button>
          <button className="knopf" disabled={!gewaehltErinnern.length || laeuft} onClick={() => erinnern(gewaehltErinnern.map((a: any) => a.id))}>
            Ausgewählte erinnern ({gewaehltErinnern.length})
          </button>
        </div>
      </div>
      <p className="klein leise" style={{ margin: '4px 0 8px' }}>
        Eine Zeile je Gutschrift, neueste oben. Rot: Push noch nicht raus. Orange: angefordert, Rechnung fehlt. Grün: Rechnung liegt vor, da geht kein Push mehr raus. Grau: wahrscheinlich keine Leistung, erst entscheiden. Der Chip klappt das Menü auf; bei Abweichungen steht der Push-Knopf nur dort.
      </p>

      {!sicht.length && <p className="klein leise">Nichts in diesem Filter.</p>}
      <div className="gl-liste">
        {sicht.map((g) => {
          const k0 = g.kopf;
          /* DER BETRAG STEHT IMMER AN DERSELBEN STELLE (.412). Marvin, 19.10.2026:
             „Entweder oder. Und wenn, dann muss es stimmen und es muss auch in
             einer Reihe stehen." Mit Geldrecht trägt jede Zeile ihre Spalte: der
             Nettobetrag der Gutschrift, sonst „ohne Betrag" (Gutschrift ohne
             gelesenen Betrag oder Freigabe ohne Gutschrift). */
          const betrag = !darfGeld ? null : k0.werte?.gutschrift != null ? euro(k0.werte.gutschrift) : 'ohne Betrag';
          const rotHier = g.zeilen.filter((z) => chipfarbe(z) === 'rot');
          const offenAngefordert = g.angefordert.filter((a) => !a.rechnung_da);
          const alleGewaehlt = (rotHier.length > 0 || offenAngefordert.length > 0)
            && rotHier.every((z) => gewaehlt.has(schluessel(z))) && offenAngefordert.every((a) => gewaehltAngefordert.has(a.id));
          return (
            <div key={g.schluessel} className={`gl-zeile ${rotHier.length ? 'offen' : offenAngefordert.length ? 'wartet' : 'da'}`}>
              <div className="gl-kopf">
                <span className="gl-wahl">
                  {(rotHier.length > 0 || offenAngefordert.length > 0) && (
                    <input type="checkbox" aria-label="alle offenen Firmen dieser Gutschrift wählen" checked={alleGewaehlt}
                           onChange={() => {
                             const n = new Set(gewaehlt); for (const z of rotHier) { if (alleGewaehlt) n.delete(schluessel(z)); else n.add(schluessel(z)); } setGewaehlt(n);
                             const e = new Set(gewaehltAngefordert); for (const a of offenAngefordert) { if (alleGewaehlt) e.delete(a.id); else e.add(a.id); } setGewaehltAngefordert(e);
                           }} />
                  )}
                </span>
                <span className="gl-gs">
                  <b>{g.nummer ? `GS ${g.nummer}` : g.zeilen.length ? 'Gutschrift ohne Nummer' : `Freigabe vom ${datum(g.angefordert[0]?.freigegeben_am)}`}</b>
                  <span className="klein leise">
                    {g.eingang ? `Eingang ${datum(g.eingang)}` : g.zeilen.length ? '' : 'vor der Umstellung auf Gutschriften (.389), ohne Gutschriftnummer'}
                    {k0.altbestand ? ', Altbestand vor dem Stichtag' : ''}
                    {k0.aus_mail ? ', aus der Mail' : k0.von_hand ? ', von Hand' : ''}
                    {k0.leb_art ? `, ${k0.leb_art === 'teil' ? 'Teil-LEB' : k0.leb_art === 'schluss' ? 'Schluss-LEB' : 'LEB komplett'}` : ''}
                    {Number(k0.gutschriften_auftrag ?? 0) > 1 ? `, ${k0.gutschriften_auftrag} Gutschriften zu diesem Auftrag` : ''}
                  </span>
                </span>
                <span className={`gl-betrag ${betrag === 'ohne Betrag' ? 'leise' : ''}`} title={betrag && betrag !== 'ohne Betrag' ? 'Nettobetrag der Gutschrift' : betrag === 'ohne Betrag' ? 'Kein Betrag an dieser Gutschrift gelesen' : undefined}>{betrag ?? ''}</span>
                <span className="gl-adresse">
                  <b>
                    {oeffne && g.auftrag_id
                      ? <button type="button" className="alslink gl-zum-auftrag" title="Akte öffnen: Bilder, Aufmaß, Verlauf" onClick={() => oeffne(g.auftrag_id, 'uebersicht')}>{voll(k0) || 'ohne Anschrift'}</button>
                      : (voll(k0) || 'ohne Anschrift')}
                    {k0.maengelbeseitigung && <span className="marke-zustand z-neu gl-mb" title="Auftrag zur Mängelbeseitigung, nicht der ursprüngliche Auftrag">Mängelbeseitigung</span>}
                  </b>
                  <NummernKopieren kls={k0.kls_id} sm={k0.sm_nummer} />
                </span>
              </div>
              <div className="gl-chips">
                {g.zeilen.map((z) => {
                  const k = schluessel(z); const f = chipfarbe(z); const l = lageVon(z);
                  const ab = (z.abweichung_zeilen ?? []).length;
                  const offen = auf.has(k);
                  return (
                    <button key={k} type="button" className={`gl-chip ${f} ${offen ? 'offen' : ''}`} aria-expanded={offen}
                            onClick={() => schalteIn(auf, setAuf, k)}>
                      <span className="gl-chip-firma">{z.firma}</span>
                      <span className="gl-chip-text">
                        {GEWERK[z.gewerk_gruppe]}{z.aus_aufmass ? <span className="gl-aus-aufmass" title="Die Firma hat am Auftrag aufgemessen, war aber nicht auf dem Gewerk gebucht">, aus dem Aufmaß</span> : null}, {f === 'gruen' ? `Rechnung ${z.rechnung_nummer ?? 'liegt vor'}`
                          : f === 'grau' ? 'keine Leistung?'
                          : ab ? `${ab} ${ab === 1 ? 'Position' : 'Positionen'} anders` : l === 'drueber' ? 'Gutschrift über dem Aufmaß' : l === 'korrigiert' ? 'Telekom hat korrigiert' : 'keine Rechnung, passt'}
                      </span>
                      <span aria-hidden="true">{offen ? '▴' : '▾'}</span>
                    </button>
                  );
                })}
                {g.angefordert.map((a) => (
                  a.rechnung_da ? (
                    <span key={a.id} className="gl-chip gruen still">
                      <span className="gl-chip-firma">{a.firma}</span>
                      <span className="gl-chip-text">{GEWERK[a.gewerk_gruppe]}, Rechnung {a.rechnung_nummer ?? 'da'}</span>
                    </span>
                  ) : (
                    <label key={a.id} className={`gl-chip orange ${gewaehltAngefordert.has(a.id) ? 'offen' : ''}`}>
                      <input type="checkbox" checked={gewaehltAngefordert.has(a.id)} aria-label="zum Erinnern wählen"
                             onChange={() => { const e = new Set(gewaehltAngefordert); if (e.has(a.id)) e.delete(a.id); else e.add(a.id); setGewaehltAngefordert(e); }} />
                      <span className="gl-chip-firma">{a.firma}</span>
                      <span className="gl-chip-text">
                        {GEWERK[a.gewerk_gruppe]}, angefordert {datum(a.freigegeben_am)}
                        {a.push_zugestellt === true ? ', Push zugestellt' : a.push_zugestellt === false ? ', Push NICHT zugestellt' : ''}
                        {a.erinnert_am ? `, erinnert ${datum(a.erinnert_am)}` : ''}
                      </span>
                      <button type="button" className="alslink klein" disabled={laeuft} onClick={(e) => { e.preventDefault(); erinnern([a.id]); }}>Erinnern</button>
                    </label>
                  )
                ))}
              </div>
              {g.zeilen.filter((z) => auf.has(schluessel(z))).map((z) => {
                const k = schluessel(z); const f = chipfarbe(z); const l = lageVon(z);
                return (
                  <div key={k} className="ga-auf gl-auf">
                    <div className="gl-auf-kopf">
                      <b>{z.firma}, {GEWERK[z.gewerk_gruppe]}</b>
                      {!!(z.pruefhinweise ?? []).length && (
                        <span className="ga-pruefen">bitte prüfen
                          <Frage text={(z.pruefhinweise as string[]).join(' ')} offen={frage === k} schalte={() => setFrage(frage === k ? null : k)} />
                        </span>
                      )}
                      {z.rechnung ? <RechnungChip r={z.rechnung} pruefen={ich ? setPruefe : undefined} /> : null}
                    </div>
                    {f === 'gruen' && (
                      <div className="hinweis fehler" style={{ margin: '0 0 8px' }}>
                        Rechnung {z.rechnung_nummer ?? ''} liegt schon zu diesem Auftrag. Kein Push: die Firma hätte sonst eine zweite geschrieben. Hier nur festhalten, geprüft wird in Schritt 2.
                      </div>
                    )}
                    <Positionsblock z={z} k={k} l={l} ohne={ohne} setOhne={setOhne} mehr={mehr} setMehr={setMehr} nicht={nicht} setNicht={setNicht}
                                    laeuft={laeuft} ohneRechnung={ohneRechnung} darfGeld={darfGeld}
                                    hinweis={hinweise[k] ?? ''} setHinweis={(t) => setHinweise({ ...hinweise, [k]: t })} />
                    {f === 'rot' && (
                      <div className="pushvorschau" style={{ marginTop: 10 }}>
                        <div className="klein leise" style={{ marginBottom: 4 }}>So geht der Push an {z.firma} raus (nur Mengen, nie Preise):</div>
                        <pre className="pushtext">{pushVorschau([vorschauZeile(z)])}</pre>
                      </div>
                    )}
                    <div className="reihe" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      {f === 'rot' && (
                        <button type="button" className="knopf haupt" disabled={laeuft} onClick={() => los([z])}>
                          Push an {z.firma} jetzt senden
                        </button>
                      )}
                      {/* DER GRAUE CHIP WIRD QUITTIERT (.392). Marvin: „dass ich das auch
                          quittiere. Ja, keine Leistung. ... dass ich den Chip wegbekomme
                          und dass ich die Gutschrift abhaken kann." Getrennte Firmen,
                          getrennt abgehakt: die Zeile bleibt, bis der letzte Chip weg ist. */}
                      {f === 'grau' && (
                        <>
                          <button type="button" className="knopf haupt" disabled={laeuft}
                                  title="Keine Leistung dieser Firma: ohne Nachricht abhaken, der Grund kommt in die Akte"
                                  onClick={() => ohneRechnung(z)}>Keine Rechnung nötig, abhaken</button>
                          <button type="button" className="knopf" disabled={laeuft} title="Doch eine Rechnung anfordern"
                                  onClick={() => los([z])}>Trotzdem Push an {z.firma}</button>
                        </>
                      )}
                      {f !== 'grau' && (
                        <button type="button" className="knopf" disabled={laeuft} title="Festhalten ohne Nachricht an die Firma"
                                onClick={() => los([z], true)}>{f === 'gruen' ? 'Festhalten, ohne Push' : 'Erledigt, ohne Nachricht'}</button>
                      )}
                      <button type="button" className="knopf" onClick={() => schalteIn(auf, setAuf, k)}>Zuklappen</button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
